import React, { useState } from 'react';
import { Form } from 'react-bootstrap';

import {
  commitKpiTitle,
  draftKpiTitle,
  PmoKpiConfig,
  reorderKpiConfigs,
  toggleKpiConfig,
} from '../../pages/projects/pmoKpiConfig';

export type ProjectsKpiCardOption = {
  id: string;
  label: string;
  description?: string;
};

interface ProjectsKpiCardPickerProps {
  options: ProjectsKpiCardOption[];
  value: PmoKpiConfig[];
  onChange: (next: PmoKpiConfig[]) => void;
  disabled?: boolean;
}

const ProjectsKpiCardPicker: React.FC<ProjectsKpiCardPickerProps> = ({
  options,
  value,
  onChange,
  disabled = false,
}) => {
  const [draggedKpiId, setDraggedKpiId] = useState<string | null>(null);
  const optionsById = options.reduce<Record<string, ProjectsKpiCardOption>>((acc, option) => {
    acc[option.id] = option;
    return acc;
  }, {});

  return (
    <>
      <div className="projects-columns-picker">
        {options.map((kpi) => (
          <Form.Check
            key={kpi.id}
            type="checkbox"
            id={`projects-kpi-${kpi.id}`}
            label={kpi.description ? `${kpi.label} — ${kpi.description}` : kpi.label}
            checked={value.some((item) => item.id === kpi.id)}
            disabled={disabled}
            onChange={() => onChange(toggleKpiConfig(value, kpi.id))}
          />
        ))}
      </div>
      {!!value.length && (
        <div className="projects-kpi-selected-list mt-2">
          {value.map((kpiConfig) => {
            const option = optionsById[kpiConfig.id];
            if (!option) return null;
            return (
              <div
                key={kpiConfig.id}
                className={`projects-kpi-selected-item${draggedKpiId === kpiConfig.id ? ' is-dragging' : ''}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const sourceId = draggedKpiId || event.dataTransfer.getData('text/plain');
                  setDraggedKpiId(null);
                  if (!sourceId || sourceId === kpiConfig.id) return;
                  onChange(reorderKpiConfigs(value, sourceId, kpiConfig.id));
                }}
              >
                <div
                  className="projects-kpi-selected-handle"
                  title="Arraste para reordenar"
                  draggable={!disabled}
                  onDragStart={(event) => {
                    setDraggedKpiId(kpiConfig.id);
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', kpiConfig.id);
                  }}
                  onDragEnd={() => setDraggedKpiId(null)}
                >
                  ⋮⋮
                </div>
                <div className="projects-kpi-selected-content">
                  <div className="small fw-semibold">{option.label}</div>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={kpiConfig.title ?? ''}
                    disabled={disabled}
                    onChange={(event) => onChange(draftKpiTitle(value, kpiConfig.id, event.target.value))}
                    onBlur={(event) => onChange(commitKpiTitle(value, kpiConfig.id, event.target.value))}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                      if (event.key === 'Enter') {
                        event.currentTarget.blur();
                      }
                    }}
                    onPointerDown={(event) => event.stopPropagation()}
                    placeholder="Título personalizado (opcional)"
                    autoComplete="off"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ProjectsKpiCardPicker;
